import * as bare from '@bare-ts/lib';
import axios from 'axios';
import { ExternalDataset } from 'bindings/reports/generic/ExternalDataset';
import { GenericReport } from 'bindings/reports/generic/GenericReport';
import { Cookies } from 'react-cookie';
import { URL_FOUNDATION } from '../../../config/keys';
import {
	readReport,
	Cell,
	ComplexCell,
	Report,
	Value
} from '../interpretor/bareReportingDecoder';

const cookies = new Cookies();
const PATH = `${URL_FOUNDATION}/api`;
const token = cookies.get('id_token') || null;


/**
 * Safely decodes a Bare-encoded Report with a custom buffer limit.
 * Bypasses the hardcoded DEFAULT_CONFIG in the auto-generated file.
 * * @param bytes The raw Uint8Array payload
 * @param maxBufferLength The maximum allowed size in bytes (defaults to 10MB)
 */
export function decodeReport(
	bytes: Uint8Array,
	maxBufferLength: number = 100 * 1024 * 1024
): Report {

	// 1. Create a custom config with our larger limit
	const customConfig = bare.Config({
		maxBufferLength: maxBufferLength
	});

	// 2. Initialize the ByteCursor using the custom config
	// This is where the old code crashed, but now it will succeed!
	const bc = new bare.ByteCursor(bytes, customConfig);

	// 3. Hand the cursor over to the auto-generated reader
	const result = readReport(bc);

	// 4. Verify we consumed exactly the expected amount of data
	if (bc.offset < bc.view.byteLength) {
		throw new bare.BareError(bc.offset, 'remaining bytes');
	}

	return result;
}

// https://mojoauth.com/hashing/fast-hash-in-javascript-in-browser
function fastHash(str) {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = (hash << 5) - hash + str.charCodeAt(i); // Hash computation
		hash |= 0; // Convert to 32bit integer
	}
	return hash >>> 0; // Ensure the result is unsigned
}

/**
 * Server-side export: the workbook is built on the server from the report
 * definition, uploaded to S3, and announced through the export websocket
 * message (download from the toolbar toast). Works whatever the report size —
 * the display cap does not apply to exports.
 */
export async function exportGenericReportServer(body: GenericReport, includeNodes: boolean, name: string): Promise<void> {
	await axios.post(`${PATH}/v2/reports/generic/export?include_nodes=${includeNodes}&name=${encodeURIComponent(name)}`, body);
}

export async function getGenericAndDecodeReport(
	body: GenericReport, userId: number | undefined,
	// skip the cached response but still refresh it: used to resync a report
	// whose displayed row ids no longer match the server's build
	bypassCache?: boolean,
	// cross-platform dashboards have no id_token cookie, so the axios default
	// Authorization is empty: they hand their token in explicitly
	bearerToken?: string
): Promise<Report> {
	const body_hash = fastHash(JSON.stringify({ body, userId }));

	// v2: row ids became deterministic (sorted grouping) — v1 responses carry
	// ids no build will produce again, and each would cost one stale-reload
	const cacheName = 'report-cache-v2';
	caches.delete('report-cache-v1').catch(() => undefined);
	const cache = await caches.open(cacheName);

	// Subtree responses are never cached: their row ids are only meaningful
	// against the build that answered the full query, and the server keeps
	// that build warm — a cached subtree could outlive it and splice another
	// build's rows into the displayed report.
	const isSubtree = body.subtree_of != null;

	// 1. Check Manual Cache
	const cachedResponse = bypassCache || isSubtree ? undefined : await cache.match(body_hash);
	if (cachedResponse) {
		const cacheControl = cachedResponse.headers.get('cache-control') || '';
		const dateHeader = cachedResponse.headers.get('date');

		// Parse max-age=XXXX
		const maxAgeMatch = cacheControl.match(/max-age=(\d+)/);
		const maxAgeSeconds = maxAgeMatch ? parseInt(maxAgeMatch[1], 10) : 0;

		if (dateHeader && maxAgeSeconds > 0) {
			const ageInSeconds = (new Date() - new Date(dateHeader)) / 1000;

			if (ageInSeconds < maxAgeSeconds) {
				const buffer = await cachedResponse.arrayBuffer();
				return decodeReport(new Uint8Array(buffer));
			}
		}
	}

	// 2. Fetch from server
	const res = await axios.post<ArrayBufferLike>(
		`${PATH}/v2/reports/generic/query${userId ? `?user_id=${userId}` : ''}`,
		body,
		{
			responseType: 'arraybuffer',
			...(bearerToken && { headers: { Authorization: `Bearer ${bearerToken}` } })
		}
	);

	if (!isSubtree) {
		await pruneCache(cache);

		// 3. Store in Cache Storage using the headers from the server
		// We pass the actual headers from the response so our logic works next time
		const headersForCache = {
			'Content-Type': res.headers['content-type'] || 'application/octet-stream',
			'Cache-Control': res.headers['cache-control'],
			Date: res.headers['date'] || new Date().toUTCString()
		};

		const responseToCache = new Response(res.data, { headers: headersForCache });
		await cache.put(body_hash, responseToCache);
	}

	return decodeReport(new Uint8Array(res.data));
}

/**
 * Iterates through the cache and deletes expired items
 */
async function pruneCache(cache: Cache) {
	const keys = await cache.keys();
	const now = new Date().getTime();

	for (const request of keys) {
		const response = await cache.match(request);
		if (!response) continue;

		const dateHeader = response.headers.get('date');
		const cacheControl = response.headers.get('cache-control') || '';
		const maxAgeMatch = cacheControl.match(/max-age=(\d+)/);

		if (dateHeader && maxAgeMatch) {
			const maxAgeSeconds = parseInt(maxAgeMatch[1], 10);
			const expiryTime = new Date(dateHeader).getTime() + maxAgeSeconds * 1000;

			if (now > expiryTime) {
				await cache.delete(request);
			}
		}
	}
}

export type GoalPath = {
  [key: string]: {
    value: Value['val'];
    index: number;
  };
} & { date?: string };

type GoalGoal = {
  date: string;
  value: number;
  dashboard: boolean;
};

export type Goal = {
  report_id: number;
  path: GoalPath;
  column_name: string;
  goals: GoalGoal[];
  granularity: string;
};

export enum GoalEvolutionCalculus {
  Default = 'default',
  Nmin1 = 'nmin1'
}

export type GoalHashMap = Map<
  string,
  {
    value: string;
    index: number;
    dashboard: boolean;
    evolution: Cell | ComplexCell | undefined;
  }
>;

export type GoalDelete = Omit<Goal, 'goals'> & { date: string };

export async function removeGoals(report_id: number): Promise<void> {
	axios.defaults.headers.common.Authorization = `${token}`;
	await axios.delete(`${PATH}/v2/goals/${report_id}`);
}
export async function getGoals(report_id: number): Promise<Goal[]> {
	axios.defaults.headers.common.Authorization = `${token}`;
	return axios.get(`${PATH}/v2/goals/${report_id}`).then(res => res.data);
}

export async function postGoals(goals: Goal[]): Promise<void> {
	axios.defaults.headers.common.Authorization = `${token}`;
	return axios.post(`${PATH}/v2/goals`, goals);
}

export async function deleteGoals(goals: GoalDelete[]): Promise<void> {
	axios.defaults.headers.common.Authorization = `${token}`;
	return axios.post(`${PATH}/v2/goals/delete`, goals);
}

export async function getExternalDatasets(): Promise<ExternalDataset[]> {
	axios.defaults.headers.common.Authorization = `${token}`;
	return axios.get<ExternalDataset[]>(`${PATH}/v2/reports/generic/external-datasets`).then(res => res.data);
}

export async function createNewAdditionalColumn(name: string): Promise<number> {
	axios.defaults.headers.common.Authorization = `${token}`;
	const res = await axios.post(`${PATH}/v2/additional-columns`, {
		name,
		type: 'ReportColumn'
	});
	return res.data;
}

export async function updateGenericReport(
	id: number,
	body: any
): Promise<void> {
	axios.defaults.headers.common.Authorization = `${token}`;
	await axios.put(`${PATH}/v2/reports/generic/${id}`, body);
}

interface ReportResponse {
  id: number;
  name: string;
  value: Report;
}

export async function deleteGenericReport(id: number): Promise<void> {
	axios.defaults.headers.common.Authorization = `${token}`;
	await axios.delete(`${PATH}/v2/reports/generic/${id}`);
}

export async function getGenericReport(id: number): Promise<ReportResponse> {
	axios.defaults.headers.common.Authorization = `${token}`;
	return axios
		.get<ReportResponse>(`${PATH}/v2/reports/generic/${id}`)
		.then(res => res.data);
}
export async function createGenericReport(body: any): Promise<number> {
	axios.defaults.headers.common.Authorization = `${token}`;
	return await axios
		.post(`${PATH}/v2/reports/generic`, body)
		.then(res => res.data);
}
export async function getReportPresignedUrl(
	report: GenericReport
): Promise<string> {
	return await axios
		.post(`${PATH}/v2/unique-token/gen`, { value: report })
		.then(res => res.data.token);
}

export type GoalImportBody = {
  panels: string[];
  goals: {
    [id: number]: Array<
      Array<{ value: number; date: string; dashboard: boolean }> | undefined
    >;
  };
};
export async function importReportGoals(
	reportId: number,
	body: GoalImportBody
): Promise<void> {
	return await axios
		.post(`${PATH}/v2/goals/${reportId}/import`, body)
		.then(res => res.data);
}
